/* eslint-disable @typescript-eslint/no-explicit-any */
 
import React, { useEffect } from 'react';
import { ArrowLeft, Save } from 'lucide-react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Environment, ContactShadows } from '@react-three/drei';
import { PhoneModel } from '@smartphone-tycoon/shared-3d';
import { Controls } from './Controls';
import { ConfigManager } from './ConfigManager';
import { ShareButton } from './ShareButton';
import { MetricsDisplay } from './business/MetricsDisplay';
import { generatePhoneName } from '../utils/nameGenerator';
import { ProductMetricsService } from '../services/ProductMetricsService';
import { formatCurrency } from '../utils/currencyFormatter';
import { useShallow } from 'zustand/react/shallow';
import { showConfirm } from '../utils/alertUtils';
import { 
  useDesignStore,
  selectActiveDesign,
  selectSelectedComponents,
  createFilteredComponentsSelector,
  createFilteredMaterialsSelector,
  getDefaultDesignConfiguration
} from '../stores/designStore';

import type { ResearchUnlock } from '@smartphone-tycoon/shared-core';

interface DesignerStudioProps {
  onBack: () => void;
  developmentMode?: boolean;
  budget?: number;
  gameDate?: { year: number; month: number; day: number };
  completedResearch?: string[];
  researchUnlocks?: ResearchUnlock[]; // Research unlocks for filtering
  onNewDesign?: (designConfig: any) => void;
  onUpdateProduct?: (updatedProduct: any) => void;
  editProduct?: any;
  fromTycoon?: boolean;
  companyName?: string;
  productCount?: number;
}

export function DesignerStudio({ 
  onBack, 
  developmentMode = false, 
  budget = 0, 
  gameDate, 
  completedResearch, 
  researchUnlocks,
  onNewDesign, 
  onUpdateProduct,
  editProduct,
  fromTycoon,
  companyName,
  productCount = 0
}: DesignerStudioProps) {
  const isEditMode = !!editProduct;
  
  // Track initialization to prevent infinite loops
  const hasInitialized = React.useRef(false);
  
  // Generate default name based on context
  const getDefaultName = React.useCallback(() => {
    if (fromTycoon && companyName) {
      // Strip common company suffixes
      const cleanCompanyName = companyName
        .replace(/\s+(Inc\.?|Incorporated|Corp\.?|Corporation|Co\.?|Company|Ltd\.?|Limited|LLC|L\.L\.C\.|PLC|P\.L\.C\.)$/i, '')
        .trim();
      
      // First product: "Company Phone"
      // Subsequent products: "Company Phone 2", "Company Phone 3", etc.
      if (productCount === 0) {
        return `${cleanCompanyName} Phone`;
      } else {
        return `${cleanCompanyName} Phone ${productCount + 1}`;
      }
    }
    return generatePhoneName();
  }, [fromTycoon, companyName, productCount]);
  
  // Get store state and actions - use shallow equality to prevent unnecessary re-renders
  const activeDesign = useDesignStore(selectActiveDesign);
  const selectedComponents = useDesignStore(
    useShallow((state) => selectSelectedComponents(state))
  );
  
  // Get all actions at once to avoid multiple subscriptions
  const actions = useDesignStore(
    useShallow((state) => ({
      batchUpdate: state.batchUpdate,
      loadConfiguration: state.loadConfiguration,
      setName: state.setName,
      setPrice: state.setPrice,
      setColor: state.setColor,
      setMaterial: state.setMaterial,
      setButtonColor: state.setButtonColor,
      setHasButtons: state.setHasButtons,
      setScreenBorderColor: state.setScreenBorderColor,
      setCameraStyle: state.setCameraStyle,
      setCameraColor: state.setCameraColor,
      setCameraPosition: state.setCameraPosition,
      setButtonPositions: state.setButtonPositions,
      setPhoneSize: state.setPhoneSize,
      setScreenSize: state.setScreenSize,
      setCornerRadius: state.setCornerRadius,
      setCpu: state.setCpu,
      setGpu: state.setGpu,
      setRam: state.setRam,
      setStorage: state.setStorage,
      setNetwork: state.setNetwork,
      setOs: state.setOs,
      setCamera: state.setCamera,
      setBattery: state.setBattery,
      setLogo: state.setLogo
    }))
  );
  
  const {
    batchUpdate,
    loadConfiguration,
    setName,
    setPrice,
    setColor,
    setMaterial,
    setButtonColor,
    setHasButtons,
    setScreenBorderColor,
    setCameraStyle,
    setCameraColor,
    setCameraPosition,
    setButtonPositions,
    setPhoneSize,
    setScreenSize,
    setCornerRadius,
    setCpu,
    setGpu,
    setRam,
    setStorage,
    setNetwork,
    setOs,
    setCamera,
    setBattery,
    setLogo
  } = actions;

  // Create filtered selectors with useMemo to prevent recreation on every render
  const filteredComponents = React.useMemo(
    () => createFilteredComponentsSelector(gameDate, researchUnlocks),
    [gameDate, researchUnlocks]
  );
  
  const filteredMaterials = React.useMemo(
    () => createFilteredMaterialsSelector(researchUnlocks, completedResearch),
    [researchUnlocks, completedResearch]
  );

  // Initialize design configuration
  useEffect(() => {
    // Skip if already initialized
    if (hasInitialized.current) {
      console.debug('[DesignerStudio] Skipping initialization - already initialized');
      return;
    }
    
    // Skip if functions are not yet available (store might still be initializing)
    if (!loadConfiguration || !batchUpdate) {
      console.debug('[DesignerStudio] Store functions not ready yet');
      return;
    }

    console.debug('[DesignerStudio] Initializing design configuration', {
      isEditMode,
      hasEditProduct: !!editProduct,
      gameDate
    });

    // Mark as initialized before making any state changes
    hasInitialized.current = true;

    if (isEditMode && editProduct && editProduct.designConfig) {
      // Load existing product configuration
      console.debug('[DesignerStudio] Loading existing product configuration');
      loadConfiguration({
        ...editProduct.designConfig,
        name: editProduct.name,
        price: editProduct.price,
        // Ensure logo has a default value for older products that don't have it
        logo: editProduct.designConfig.logo ?? { type: 'none' },
      });
    } else {
      // Set defaults for new design
      // Get default configuration based on game date
      console.debug('[DesignerStudio] Setting default configuration');
      const defaultConfig = getDefaultDesignConfiguration(gameDate, completedResearch);
      const defaultName = getDefaultName();
      batchUpdate({
        ...defaultConfig,
        name: defaultName,
        price: 399,
      });
    }
  // TODO: Fix react-hooks/exhaustive-deps - Missing dependency: getDefaultName
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, editProduct, loadConfiguration, batchUpdate, gameDate, completedResearch]);

  // Reset initialization flag when component unmounts or mode changes
  useEffect(() => {
    return () => {
      hasInitialized.current = false;
    };
  }, [isEditMode]);

  const totalCost = ProductMetricsService.calculateProductionCost(selectedComponents);
  const techScore = ProductMetricsService.calculateTechScore(selectedComponents);
  const valueScore = ProductMetricsService.calculateValueScore(techScore, activeDesign.price);
  const profit = activeDesign.price - totalCost;
  const userSatisfactionScore = ProductMetricsService.calculateUserSatisfaction(techScore, valueScore);

  const handleBack = async () => {
    if (developmentMode) {
      const result = await showConfirm(
        'All progress will be lost.',
        'Are you sure you want to cancel?',
        {
          confirmButtonText: 'Yes, cancel',
          cancelButtonText: 'No, keep working',
          icon: 'warning'
        }
      );
      if (result.isConfirmed) {
        onBack();
      }
    } else {
      onBack();
    }
  };

  const handleFinishDesign = () => {
    const designConfig = {
      ...activeDesign,
      components: selectedComponents,
      techScore: techScore,
    };

    if (developmentMode) {
      if (isEditMode && onUpdateProduct) {
        onUpdateProduct({ ...editProduct, designConfig, name: activeDesign.name, price: activeDesign.price });
      } else if (onNewDesign) {
        onNewDesign(designConfig);
      }
      // Don't call onBack here - let the parent component handle navigation
    }
  };

  return (
    <div className="min-h-screen bg-theme-bg-secondary">
      <div className="bg-theme-bg-content shadow-theme">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-6">
          <div className="relative flex items-center h-16">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 text-theme-text-secondary hover:text-theme-text-primary transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              {developmentMode ? (isEditMode ? 'Cancel Edit' : 'Cancel Design') : 'Back to Menu'}
            </button>
            <div className="absolute left-1/2 transform -translate-x-1/2">
              <h1 className="text-xl font-semibold text-theme-text-primary">Designer Studio</h1>
            </div>
            {developmentMode && (
              <div className="ml-auto">
                <button
                  onClick={handleFinishDesign}
                  data-testid="save-product"
                  className="flex items-center gap-2 px-4 py-2 bg-theme-success text-white rounded-lg hover:bg-theme-success/80 transition-colors"
                >
                  <Save className="w-5 h-5" />
                  {isEditMode ? 'Update Product' : 'Finish Design'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="w-full h-[calc(100vh-4rem)] flex">
        <div className="flex-1 relative">
          <Canvas shadows camera={{ position: [0, 0, 10], fov: 45 }}>
            <ambientLight intensity={0.5} />
            <spotLight
              position={[10, 10, 10]}
              angle={0.15}
              penumbra={1}
              intensity={1}
              castShadow
            />
            <PhoneModel
              color={activeDesign.color}
              material={activeDesign.material}
              buttonColor={activeDesign.buttonColor}
              hasButtons={activeDesign.hasButtons}
              screenBorderColor={activeDesign.screenBorderColor}
              cameraStyle={activeDesign.cameraStyle}
              cameraColor={activeDesign.cameraColor}
              cameraPosition={activeDesign.cameraPosition}
              buttonPositions={activeDesign.buttonPositions}
              phoneSize={activeDesign.phoneSize}
              screenSize={activeDesign.screenSize}
              cornerRadius={activeDesign.cornerRadius}
              logo={activeDesign.logo}
            />
            <Environment preset="city" />
            <ContactShadows
              position={[0, -2.5, 0]}
              opacity={0.4}
              scale={20}
              blur={1.5}
              far={4.5}
            />
            <OrbitControls
              minPolarAngle={Math.PI / 3}
              maxPolarAngle={Math.PI / 2}
              enableZoom={true}
              enablePan={false}
            />
          </Canvas>

          <ConfigManager
            config={activeDesign}
            onConfigLoad={loadConfiguration}
          />

          {!developmentMode && (
            <div className="absolute top-4 right-4">
              <ShareButton
                name={activeDesign.name}
                price={activeDesign.price}
                specs={selectedComponents}
                previewConfig={{
                  color: activeDesign.color,
                  material: activeDesign.material,
                  buttonColor: activeDesign.buttonColor,
                  hasButtons: activeDesign.hasButtons,
                  screenBorderColor: activeDesign.screenBorderColor,
                  cameraStyle: activeDesign.cameraStyle,
                  cameraColor: activeDesign.cameraColor,
                  cameraPosition: activeDesign.cameraPosition,
                  buttonPositions: activeDesign.buttonPositions,
                  phoneSize: activeDesign.phoneSize,
                  screenSize: activeDesign.screenSize,
                  cornerRadius: activeDesign.cornerRadius,
                  logo: activeDesign.logo,
                }}
                shareableUrl={window.location.href}
              />
            </div>
          )}

          {/* Metrics Display - centered at bottom */}
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2">
            <MetricsDisplay
              totalCost={totalCost}
              price={activeDesign.price}
              profit={profit}
              techScore={techScore}
              userSatisfaction={userSatisfactionScore}
            />
          </div>

          {/* Budget Display - centered at top */}
          {developmentMode && (
            <div className="absolute top-4 left-1/2 transform -translate-x-1/2">
              <div className="bg-theme-bg-content px-6 py-3 rounded-theme shadow-theme">
                <div className="text-center">
                  <p className="text-sm text-theme-text-muted">Development Budget</p>
                  <p className="text-lg font-semibold">{formatCurrency(budget || 0)}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="w-96 bg-theme-bg-content shadow-theme overflow-y-auto">
          <Controls
            name={activeDesign.name}
            setName={setName}
            price={activeDesign.price}
            setPrice={setPrice}
            color={activeDesign.color}
            setColor={setColor}
            material={activeDesign.material}
            setMaterial={setMaterial}
            buttonColor={activeDesign.buttonColor}
            setButtonColor={setButtonColor}
            hasButtons={activeDesign.hasButtons}
            setHasButtons={setHasButtons}
            screenBorderColor={activeDesign.screenBorderColor}
            setScreenBorderColor={setScreenBorderColor}
            cameraStyle={activeDesign.cameraStyle}
            setCameraStyle={setCameraStyle}
            cameraColor={activeDesign.cameraColor}
            setCameraColor={setCameraColor}
            cameraPosition={activeDesign.cameraPosition}
            setCameraPosition={setCameraPosition}
            buttonPositions={activeDesign.buttonPositions}
            setButtonPositions={setButtonPositions}
            phoneSize={activeDesign.phoneSize}
            setPhoneSize={setPhoneSize}
            screenSize={activeDesign.screenSize}
            setScreenSize={setScreenSize}
            cornerRadius={activeDesign.cornerRadius}
            setCornerRadius={setCornerRadius}
            cpu={activeDesign.cpu}
            setCpu={setCpu}
            gpu={activeDesign.gpu}
            setGpu={setGpu}
            ram={activeDesign.ram}
            setRam={setRam}
            storage={activeDesign.storage}
            setStorage={setStorage}
            network={activeDesign.network}
            setNetwork={setNetwork}
            os={activeDesign.os}
            setOs={setOs}
            camera={activeDesign.camera}
            setCamera={setCamera}
            battery={activeDesign.battery}
            setBattery={setBattery}
            logo={activeDesign.logo}
            setLogo={setLogo}
            gameDate={gameDate}
            completedResearch={completedResearch}
            filteredMaterials={filteredMaterials}
            filteredComponents={filteredComponents}
          />
        </div>
      </div>
    </div>
  );
}